import React, { useEffect, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { Vendor, VendorFile } from '../types';
import { Modal } from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast, ToastMessage } from '../components/Toast';
import { Store, Plus, Trash2, Edit2, Phone, Mail, PlusCircle, Paperclip, X, Download } from 'lucide-react';
import { syncVendorToBudget } from '../lib/budget';
import { slugify } from '../constants';
import { Button } from '../components/ui/Button';

const VENDOR_FILES_BUCKET = 'vendor-files';
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_FILES = 3;

const INITIAL_VENDOR: Partial<Vendor> = {
  name: '',
  type: 'Ostatní',
  status: 'new',
  price_estimate: 0,
  paid_amount: 0,
  files: []
};

function getAllowedExtensionsForInput(): string {
  return ALLOWED_EXTENSIONS.join(',');
}

function getFileExtension(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}

function isAllowedFile(file: File): { ok: boolean; error?: string } {
  const ext = getFileExtension(file.name);
  if (!ALLOWED_EXTENSIONS.includes(ext)) return { ok: false, error: 'file_format_error' };
  if (file.size > MAX_FILE_SIZE) return { ok: false, error: 'file_size_error' };
  return { ok: true };
}

/** Normalize files field – handles null, undefined, and legacy single-file data */
function normalizeFiles(vendor: Partial<Vendor>): VendorFile[] {
  if (Array.isArray(vendor.files)) return vendor.files;
  // Legacy: single file_name/file_path (shouldn't happen after migration, but just in case)
  const legacy = vendor as any;
  if (legacy.file_path) return [{ name: legacy.file_name || '', path: legacy.file_path }];
  return [];
}

export default function Vendors() {
  const { wedding, locale } = useWedding();
  const t = (key: string) => getTranslation(locale, key);

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Vendor>>(INITIAL_VENDOR);
  // Multi-file state
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [filesToRemove, setFilesToRemove] = useState<VendorFile[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchVendors = async () => {
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const { data } = await supabase.from('vendors').select('*').eq('wedding_id', wedding.id);
    if (data) setVendors(data);
  };

  useEffect(() => {
    fetchVendors();
  }, [wedding]);

  /** Existing files that will remain (not marked for removal) */
  const existingFiles = normalizeFiles(formData).filter(
    f => !filesToRemove.some(r => r.path === f.path)
  );
  /** Total count after save = kept existing + new uploads */
  const totalFilesAfterSave = existingFiles.length + filesToUpload.length;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();

    // Validate all new files
    for (const file of filesToUpload) {
      const check = isAllowedFile(file);
      if (!check.ok) {
        showToast('error', t(`vendors.${check.error}`));
        return;
      }
    }

    if (totalFilesAfterSave > MAX_FILES) {
      showToast('error', t('vendors.max_files_error'));
      return;
    }

    let vendorId = formData.id ?? null;
    const { files: _existingFiles, ...restForm } = formData;

    // Remove files marked for deletion from storage
    if (filesToRemove.length > 0) {
      const pathsToRemove = filesToRemove.map(f => f.path).filter(Boolean);
      if (pathsToRemove.length > 0) {
        await supabase.storage.from(VENDOR_FILES_BUCKET).remove(pathsToRemove);
      }
    }

    // Start with kept existing files
    const finalFiles: VendorFile[] = [...existingFiles];

    // Upload new files
    if (filesToUpload.length > 0) {
      try {
        // Ensure vendor row exists (for new vendors)
        if (!vendorId) {
          const ins = await supabase.from('vendors').insert({ ...restForm, files: [], wedding_id: wedding.id }).select();
          if (ins.error) { showToast('error', `Chyba: ${ins.error.message}`); return; }
          vendorId = ins.data?.[0]?.id;
          if (!vendorId) { showToast('error', 'Chyba při ukládání'); return; }
        }

        for (const file of filesToUpload) {
          const ext = getFileExtension(file.name);
          const uniqueId = crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          const storagePath = `${String(wedding.id).trim()}/${String(vendorId).trim()}/${uniqueId}${ext}`;
          const up = await supabase.storage.from(VENDOR_FILES_BUCKET).upload(storagePath, file, { upsert: true });
          if (up.error) { showToast('error', `Chyba nahrání ${file.name}: ${up.error.message}`); return; }
          finalFiles.push({ name: file.name, path: storagePath });
        }
      } catch (err) {
        showToast('error', err instanceof Error ? err.message : 'Chyba při nahrávání souboru');
        return;
      }
    }

    const payload = { ...restForm, files: finalFiles, wedding_id: wedding.id };
    let result;
    if (formData.id) {
      result = await supabase.from('vendors').update(payload).eq('id', formData.id).select();
    } else if (vendorId) {
      result = await supabase.from('vendors').update(payload).eq('id', vendorId).select();
    } else {
      result = await supabase.from('vendors').insert(payload).select();
    }
    if (result.error) { showToast('error', `Chyba: ${result.error.message}`); return; }
    if (result.data?.[0]) await syncVendorToBudget(result.data[0]);
    showToast('success', t('common.save'));
    setIsModalOpen(false);
    setFilesToUpload([]);
    setFilesToRemove([]);
    fetchVendors();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const supabase = getAuthenticatedSupabase();
    // Also remove all files from storage
    const vendor = vendors.find(v => v.id === deleteId);
    if (vendor) {
      const files = normalizeFiles(vendor);
      if (files.length > 0) {
        await supabase.storage.from(VENDOR_FILES_BUCKET).remove(files.map(f => f.path));
      }
    }
    const { error } = await supabase.from('vendors').delete().eq('id', deleteId);
    setDeleteId(null);
    if (error) showToast('error', `Chyba: ${error.message}`);
    else fetchVendors();
  };

  const handleAddNew = (type?: string) => {
    setFormData({ ...INITIAL_VENDOR, type: type || 'Ostatní' });
    setFilesToUpload([]);
    setFilesToRemove([]);
    setIsModalOpen(true);
  };

  const openEdit = (vendor: Vendor) => {
    setFormData(vendor);
    setFilesToUpload([]);
    setFilesToRemove([]);
    setIsModalOpen(true);
  };

  const handleDownloadAttachment = async (filePath: string, fileName?: string) => {
    if (!filePath?.trim()) {
      showToast('error', 'Nepodařilo se stáhnout soubor');
      return;
    }
    try {
      const supabase = getAuthenticatedSupabase();
      const { data, error } = await supabase.storage.from(VENDOR_FILES_BUCKET).download(filePath.trim());
      if (error || !data) {
        showToast('error', error?.message || 'Nepodařilo se stáhnout soubor');
        return;
      }
      const blobUrl = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName || filePath.split('/').pop() || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Nepodařilo se stáhnout soubor');
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length === 0) return;

    const available = MAX_FILES - totalFilesAfterSave;
    if (available <= 0) {
      showToast('error', t('vendors.max_files_error'));
      return;
    }
    const toAdd = newFiles.slice(0, available);
    for (const file of toAdd) {
      const check = isAllowedFile(file);
      if (!check.ok) {
        showToast('error', t(`vendors.${check.error}`));
        return;
      }
    }
    setFilesToUpload(prev => [...prev, ...toAdd]);
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeNewFile = (index: number) => {
    setFilesToUpload(prev => prev.filter((_, i) => i !== index));
  };

  const markExistingFileForRemoval = (file: VendorFile) => {
    setFilesToRemove(prev => [...prev, file]);
  };

  const unmarkExistingFileForRemoval = (file: VendorFile) => {
    setFilesToRemove(prev => prev.filter(f => f.path !== file.path));
  };

  if (!wedding) return null;

  const categories = wedding.vendor_types || [];
  const groupedVendors = categories.reduce((acc, cat) => {
    acc[cat] = vendors.filter(v => v.type === cat);
    return acc;
  }, {} as Record<string, Vendor[]>);

  return (
    <div className="space-y-12 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('nav.vendors')}</h1>
          <p className="text-ds-body text-slate-500 dark:text-slate-400 mt-1">Spravujte své dodavatele a jejich kontakty.</p>
        </div>
      </div>

       {categories.map(category => {
          const catVendors = groupedVendors[category] || [];
          return (
             <div key={category} id={slugify(category)} className="scroll-mt-24">
                <div className="flex items-center justify-between mb-6">
                   <h2 className="text-ds-h2 text-slate-900 dark:text-white">{category}</h2>
                   <Button 
                    onClick={() => handleAddNew(category)} 
                    variant="secondary"
                    icon={<PlusCircle size={18} />}
                   >
                    {t('common.add')}
                   </Button>
                </div>
                
                {catVendors.length === 0 ? (
                   <EmptyState
                     icon={Store}
                     title={`Zatím žádní dodavatelé v kategorii ${category}`}
                     action={{
                       label: t('common.add'),
                       onClick: () => handleAddNew(category)
                     }}
                   />
                ) : (
                   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {catVendors.map(vendor => {
                         const vendorFiles = normalizeFiles(vendor);
                         return (
                         <div key={vendor.id} className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-ds-border transition-all group">
                            <div className="flex justify-between items-start mb-4">
                               <div className="flex flex-wrap gap-2 items-center">
                                  <span className={`px-2.5 py-1 rounded-full text-ds-meta ${
                                    vendor.status === 'booked' ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' :
                                    vendor.status === 'canceled' ? 'bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400' :
                                    vendor.status === 'new' ? 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300' :
                                    vendor.status === 'contacted' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                  }`}>
                                    {vendor.status === 'booked' ? 'Rezervováno' : vendor.status === 'canceled' ? 'Zrušeno' : vendor.status === 'new' ? 'Nový' : vendor.status === 'contacted' ? 'Kontaktováno' : 'Poptáváme'}
                                  </span>
                                  {vendorFiles.length > 0 && (
                                    <span
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-ds-meta font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                                      title={vendorFiles.map(f => f.name).join(', ')}
                                    >
                                      <Paperclip size={12} /> {vendorFiles.length}
                                    </span>
                                  )}
                               </div>
                               <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => openEdit(vendor)} className="p-1.5 text-slate-400 hover:text-brand-600"><Edit2 size={16} /></button>
                                  <button onClick={() => setDeleteId(vendor.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                               </div>
                            </div>
                            
                            <h3 className="text-ds-h3 text-slate-900 dark:text-white mb-1">{vendor.name}</h3>
                            <div className="space-y-1 text-sm text-slate-500 mb-6">
                               {vendor.contact_name && <p>{vendor.contact_name}</p>}
                               {vendor.contact_email && <p className="flex items-center gap-2"><Mail size={14} /> {vendor.contact_email}</p>}
                               {vendor.contact_phone && <p className="flex items-center gap-2"><Phone size={14} /> {vendor.contact_phone}</p>}
                               {vendor.notes && (
                                 <p className="text-slate-500 dark:text-slate-400 line-clamp-2 mt-2">
                                   {vendor.notes.length > 100 ? `${vendor.notes.slice(0, 100)}…` : vendor.notes}
                                 </p>
                               )}
                            </div>

                            {/* File list on tile */}
                            {vendorFiles.length > 0 && (
                              <div className="space-y-1 mb-4">
                                {vendorFiles.map((f, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    onClick={() => handleDownloadAttachment(f.path, f.name)}
                                    className="flex items-center gap-2 text-sm text-brand-600 dark:text-brand-400 hover:underline truncate w-full text-left"
                                    title={f.name}
                                  >
                                    <Download size={12} className="shrink-0" />
                                    <span className="truncate">{f.name || f.path.split('/').pop()}</span>
                                  </button>
                                ))}
                              </div>
                            )}

                            <div className="pt-4 border-t border-slate-50 dark:border-slate-800 flex justify-between items-center text-sm font-medium">
                               <span className="text-slate-400">Rozpočet:</span>
                               <span className="text-slate-900 dark:text-white">{(vendor.price_estimate || 0).toLocaleString()} Kč</span>
                            </div>
                         </div>
                      );})}
                   </div>
                )}
             </div>
          );
       })}

       <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={formData.id ? 'Upravit dodavatele' : 'Nový dodavatel'}>
         <form onSubmit={handleSave} className="space-y-5">
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">Název firmy / Jméno</label>
               <input type="text" required value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200" />
            </div>
            <div className="grid grid-cols-2 gap-4">
               <div>
                  <label className="text-ds-meta text-slate-500 mb-2 block">Kategorie</label>
                  <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200">
                     {wedding.vendor_types?.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
               </div>
               <div>
                  <label className="text-ds-meta text-slate-500 mb-2 block">Stav</label>
                  <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value as Vendor['status']})} className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200">
                     <option value="new">Nový</option>
                     <option value="potential">Poptáváme</option>
                     <option value="contacted">Kontaktováno</option>
                     <option value="booked">Rezervováno</option>
                     <option value="canceled">Zrušeno</option>
                  </select>
               </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
               <div>
                  <label className="text-ds-meta text-slate-500 mb-2 block">Odhad ceny</label>
                  <input type="number" value={formData.price_estimate || 0} onChange={e => setFormData({ ...formData, price_estimate: parseFloat(e.target.value) })} className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200" />
               </div>
               <div>
                  <label className="text-ds-meta text-slate-500 mb-2 block">{t('budget.paid')}</label>
                  <input type="number" value={formData.paid_amount || 0} onChange={e => setFormData({ ...formData, paid_amount: parseFloat(e.target.value) })} className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200" />
               </div>
            </div>
             <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">Kontaktní email</label>
               <input type="email" value={formData.contact_email || ''} onChange={e => setFormData({ ...formData, contact_email: e.target.value })} className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200" />
            </div>
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">{t('common.note')}</label>
               <textarea
                 value={formData.notes ?? ''}
                 onChange={e => setFormData({ ...formData, notes: e.target.value })}
                 rows={3}
                 className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200 resize-y"
                 placeholder={t('common.note')}
               />
            </div>

            {/* ===== Attachments section (up to 3 files) ===== */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
               <label className="text-ds-meta text-slate-500 mb-2 block">
                 {t('vendors.attachments')} ({totalFilesAfterSave}/{MAX_FILES})
               </label>
               <p className="text-ds-body text-slate-500 dark:text-slate-400 mb-3">PDF, JPG, PNG, GIF, WEBP (max 5 MB / soubor)</p>

               {/* Existing files */}
               {normalizeFiles(formData).map((file, idx) => {
                 const isMarkedForRemoval = filesToRemove.some(r => r.path === file.path);
                 return (
                   <div
                     key={file.path || idx}
                     className={`flex items-center justify-between gap-2 p-3 rounded-xl border mb-2 ${
                       isMarkedForRemoval
                         ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 opacity-60'
                         : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                     }`}
                   >
                     <span className={`text-sm truncate ${isMarkedForRemoval ? 'line-through text-red-400' : 'text-slate-700 dark:text-slate-200'}`}>
                       {file.name || file.path.split('/').pop()}
                     </span>
                     <div className="flex gap-1 shrink-0">
                       {!isMarkedForRemoval ? (
                         <>
                           <Button type="button" variant="ghost" size="sm" onClick={() => handleDownloadAttachment(file.path, file.name)}>
                             {t('vendors.download')}
                           </Button>
                           <Button type="button" variant="ghost" size="sm" onClick={() => markExistingFileForRemoval(file)}>
                             <X size={14} />
                           </Button>
                         </>
                       ) : (
                         <Button type="button" variant="ghost" size="sm" onClick={() => unmarkExistingFileForRemoval(file)}>
                           Vrátit
                         </Button>
                       )}
                     </div>
                   </div>
                 );
               })}

               {/* New files to upload */}
               {filesToUpload.map((file, idx) => (
                 <div
                   key={`new-${idx}`}
                   className="flex items-center justify-between gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 mb-2"
                 >
                   <span className="text-sm text-blue-700 dark:text-blue-300 truncate">{file.name}</span>
                   <Button type="button" variant="ghost" size="sm" onClick={() => removeNewFile(idx)}>
                     <X size={14} />
                   </Button>
                 </div>
               ))}

               {/* Add file button */}
               {totalFilesAfterSave < MAX_FILES && (
                 <div className="mt-2">
                   <input
                     ref={fileInputRef}
                     type="file"
                     accept={getAllowedExtensionsForInput()}
                     onChange={handleFileInputChange}
                     className="hidden"
                     multiple
                   />
                   <Button
                     type="button"
                     variant="secondary"
                     size="sm"
                     icon={<PlusCircle size={14} />}
                     onClick={() => fileInputRef.current?.click()}
                   >
                     {t('vendors.add_file')}
                   </Button>
                 </div>
               )}
            </div>

            <Button type="submit" className="w-full">{t('common.save')}</Button>
         </form>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onCancel={() => setDeleteId(null)} onConfirm={handleDelete} title={t('common.delete')} description={t('common.confirm_delete')} />

      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
